import { useState, useRef, useEffect, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { useActiveAthlete } from "@/hooks/useActiveAthlete";
import { queryClient } from "@/lib/queryClient";
import { apiRequest } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import MediaItem from "@/components/MediaItem";
import UploadMediaForm from "@/components/UploadMediaForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, Search, Filter, Image, Video, FileText, MicIcon, RefreshCw, Star } from "lucide-react";

const legacyTheme: CSSProperties = {
  "--background": "220 58% 9%",
  "--foreground": "0 0% 100%",
  "--card": "221 45% 14%",
  "--card-foreground": "0 0% 100%",
  "--muted": "221 35% 18%",
  "--muted-foreground": "215 20% 80%",
  "--border": "40 70% 55%",
  "--input": "221 25% 20%",
  "--ring": "40 70% 55%",
};

const MediaLibrary = () => {
  const { user } = useAuth();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mediaTypeFilter, setMediaTypeFilter] = useState("all");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<string[]>([]);
  
  // Pull-to-refresh functionality
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullStartY, setPullStartY] = useState(0);
  const [pullMoveY, setPullMoveY] = useState(0);
  const pullThreshold = 80; // Pixels needed to trigger refresh
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Current media item being viewed (for swipe navigation)
  const [focusedMediaItemIndex, setFocusedMediaItemIndex] = useState<number | null>(null);
  const [swipeDirection, setSwipeDirection] = useState<string | null>(null);
  const [swipeStart, setSwipeStart] = useState<{x: number, y: number} | null>(null);
  const [showSwipeIndicator, setShowSwipeIndicator] = useState(false);

  // Get the active athlete (shared across the app; switchable)
  const { athlete } = useActiveAthlete();

  // Get media items
  const { data: mediaItems, isLoading, refetch } = useQuery({
    queryKey: ["/api/athletes", athlete?.id, "media"],
    enabled: !!athlete?.id,
  });
  
  // Handle pull-to-refresh gesture
  const handleTouchStart = (e: React.TouchEvent) => {
    // Only enable pull to refresh when at the top of the page
    if (window.scrollY <= 10) {
      setPullStartY(e.touches[0].clientY);
      setSwipeStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (pullStartY === 0 || window.scrollY > 10) return;
    
    const currentY = e.touches[0].clientY;
    const pullDistance = currentY - pullStartY;
    
    // Only allow pulling down, not up
    if (pullDistance > 0) {
      setPullMoveY(pullDistance);
      // Prevent default scrolling when pulling
      if (pullDistance > 10) {
        e.preventDefault();
      }
    }
    
    // Handle horizontal swipe for gallery navigation when a media item is focused
    if (swipeStart && focusedMediaItemIndex !== null && filteredMedia?.length > 0) {
      const currentX = e.touches[0].clientX;
      const swipeDistanceX = currentX - swipeStart.x;
      
      // Detect horizontal swipe if distance is significant
      if (Math.abs(swipeDistanceX) > 30) {
        setSwipeDirection(swipeDistanceX > 0 ? 'right' : 'left');
        setShowSwipeIndicator(true);
      } else {
        setSwipeDirection(null);
        setShowSwipeIndicator(false);
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Handle pull to refresh
    if (pullMoveY > pullThreshold && !isRefreshing) {
      // Trigger refresh
      setIsRefreshing(true);
      
      // Refresh data
      refetch().then(() => {
        // After data is refreshed
        setTimeout(() => {
          setPullMoveY(0);
          setIsRefreshing(false);
        }, 800);
      });
    } else {
      // Reset pull distance if threshold not reached
      setPullMoveY(0);
    }
    
    // Handle media navigation swipe
    if (swipeDirection && focusedMediaItemIndex !== null && filteredMedia?.length > 0) {
      const newIndex = swipeDirection === 'left' 
        ? Math.min(filteredMedia.length - 1, focusedMediaItemIndex + 1) 
        : Math.max(0, focusedMediaItemIndex - 1);
      
      if (newIndex !== focusedMediaItemIndex) {
        setFocusedMediaItemIndex(newIndex);
        // Optional: Animate or scroll to the new item
      }
    }
    
    // Reset states
    setPullStartY(0);
    setSwipeStart(null);
    setSwipeDirection(null);
    setShowSwipeIndicator(false);
  };

  // Filter media items based on search, type, and favorites
  const filteredMedia = mediaItems?.filter((media: any) => {
    // Search filter
    const matchesSearch = 
      searchQuery === "" || 
      media.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      media.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
    // Media type filter
    const matchesType = 
      mediaTypeFilter === "all" || 
      media.mediaType === mediaTypeFilter;
      
    // Favorites filter
    const matchesFavorites = !showFavoritesOnly || media.isFavorite;
      
    return matchesSearch && matchesType && matchesFavorites;
  }) || [];

  // Group media by date
  const groupedMedia = filteredMedia.reduce((groups: any, media: any) => {
    const date = new Date(media.date || media.createdAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
    
    if (!groups[date]) {
      groups[date] = [];
    }
    
    groups[date].push(media);
    return groups;
  }, {});

  const toggleMediaSelection = (mediaId: string) => {
    if (selectedMedia.includes(mediaId)) {
      setSelectedMedia(selectedMedia.filter(id => id !== mediaId));
    } else {
      setSelectedMedia([...selectedMedia, mediaId]);
    }
  };

  return (
    <div 
      className="container mx-auto min-h-screen px-4 py-6 text-white"
      style={legacyTheme}
      ref={contentRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull-to-refresh indicator */}
      {pullMoveY > 0 && (
        <div 
          className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center border-b border-[#415A77]/60 bg-[#0B1220]/90 transition-all duration-200 backdrop-blur-md"
          style={{ 
            height: `${Math.min(pullMoveY * 0.6, pullThreshold)}px`,
            opacity: pullMoveY / pullThreshold
          }}
        >
          <div className={`flex items-center ${isRefreshing ? 'animate-pulse' : ''}`}>
            <RefreshCw className={`mr-2 h-5 w-5 text-primary ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="text-sm font-medium text-slate-100">
              {isRefreshing ? 'Refreshing...' : pullMoveY > pullThreshold ? 'Release to refresh' : 'Pull to refresh'}
            </span>
          </div>
        </div>
      )}
      
      {/* Swipe navigation indicators */}
      {showSwipeIndicator && (
        <div 
          className={`fixed top-1/2 transform -translate-y-1/2 z-50 bg-black/40 text-white p-4 rounded-full transition-all duration-200 ${
            swipeDirection === 'left' ? 'right-6 animate-pulse' : 'left-6 animate-pulse'
          }`}
        >
          {swipeDirection === 'left' ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          )}
        </div>
      )}
      
      <div className="mb-6 flex flex-col gap-4 rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] px-4 py-4 shadow-lg shadow-black/25 md:flex-row md:items-center md:justify-between">
        <h1 className="font-montserrat font-bold text-2xl md:text-3xl text-white">Media Library</h1>
        
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#E0A458] text-black hover:bg-[#f0b467] flex items-center font-semibold">
              <Upload className="h-4 w-4 mr-2" /> Upload Media
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl border-[#E0A458]/40 bg-[#111827] text-slate-100 shadow-2xl max-h-[88vh] overflow-y-auto">
            <UploadMediaForm
              athleteId={athlete?.id}
              onSuccess={() => setUploadDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-3 h-4 w-4 text-neutral-400" />
              <Input
                placeholder="Search media..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Select
                value={mediaTypeFilter}
                onValueChange={setMediaTypeFilter}
              >
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="image">Images</SelectItem>
                  <SelectItem value="video">Videos</SelectItem>
                  <SelectItem value="pdf">Documents</SelectItem>
                  <SelectItem value="audio">Audio</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant={showFavoritesOnly ? "default" : "outline"}
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className="whitespace-nowrap"
              >
                <Star className={`h-4 w-4 mr-2 ${showFavoritesOnly ? 'fill-current' : ''}`} />
                {showFavoritesOnly ? "Show All" : "Favorites Only"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="grid">
        <div className="flex justify-between items-center mb-4">
          <TabsList>
            <TabsTrigger value="grid">Grid View</TabsTrigger>
            <TabsTrigger value="list">List View</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
          </TabsList>
          
          {selectedMedia.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-300">{selectedMedia.length} selected</span>
              <Button size="sm" variant="outline">Share</Button>
              <Button size="sm" variant="destructive">Delete</Button>
            </div>
          )}
        </div>

        <TabsContent value="grid">
          {isLoading ? (
            <Card>
              <CardContent className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-slate-300">Loading your memories...</p>
              </CardContent>
            </Card>
          ) : filteredMedia.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredMedia.map((mediaItem: any) => (
                <MediaItem
                  key={mediaItem.id}
                  item={mediaItem}
                  thumbnailHeight="h-52"
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-10 text-center flex flex-col items-center gap-3">
                <div className="mb-2 mx-auto rounded-full bg-[#0B1220] p-4 inline-block">
                  {mediaTypeFilter === "image" ? (
                    <Image className="h-8 w-8 text-[#E0A458]/50" />
                  ) : mediaTypeFilter === "video" ? (
                    <Video className="h-8 w-8 text-[#E0A458]/50" />
                  ) : mediaTypeFilter === "pdf" ? (
                    <FileText className="h-8 w-8 text-[#E0A458]/50" />
                  ) : mediaTypeFilter === "audio" ? (
                    <MicIcon className="h-8 w-8 text-[#E0A458]/50" />
                  ) : (
                    <Upload className="h-8 w-8 text-[#E0A458]/50" />
                  )}
                </div>
                <p className="text-slate-200 font-semibold text-lg">
                  {searchQuery ? "No results found" : "The vault is empty — for now."}
                </p>
                <p className="text-slate-400 text-sm max-w-sm">
                  {searchQuery
                    ? "Try a different search term or clear your filters."
                    : "Every photo, every video, every moment deserves a home. Start uploading and build the highlight reel of their journey."}
                </p>
                {!searchQuery && (
                  <Button
                    className="mt-3 bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold"
                    onClick={() => setUploadDialogOpen(true)}
                  >
                    <Upload className="h-4 w-4 mr-2" /> Upload Your First Memory
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="list">
          {isLoading ? (
            <Card>
              <CardContent className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-slate-300">Loading media library...</p>
              </CardContent>
            </Card>
          ) : filteredMedia.length > 0 ? (
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-[#0B1220] border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Media</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Size</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#415A77]/40">
                    {filteredMedia.map((mediaItem: any) => (
                      <tr 
                        key={mediaItem.id}
                        className="hover:bg-[#0B1220]"
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              {mediaItem.mediaType === "image" ? (
                                <img 
                                  src={mediaItem.mediaUrl}
                                  alt={mediaItem.title}
                                  className="h-10 w-10 rounded object-cover"
                                />
                              ) : mediaItem.mediaType === "video" ? (
                                <div className="h-10 w-10 rounded overflow-hidden relative">
                                  <video 
                                    src={mediaItem.mediaUrl}
                                    className="h-10 w-10 object-cover"
                                    preload="metadata"
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                    </svg>
                                  </div>
                                </div>
                              ) : (
                                <div className="h-10 w-10 rounded bg-[#0B1220] flex items-center justify-center">
                                  <span className="material-icons text-slate-400">
                                    {mediaItem.mediaType === "pdf" ? "description" :
                                     mediaItem.mediaType === "audio" ? "mic" : "insert_drive_file"}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-slate-100 truncate max-w-xs">{mediaItem.title}</p>
                              {mediaItem.description && (
                                <p className="text-xs text-slate-400 truncate max-w-xs">{mediaItem.description}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-400">
                          {mediaItem.mediaType.charAt(0).toUpperCase() + mediaItem.mediaType.slice(1)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-400">
                          {new Date(mediaItem.date || mediaItem.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-400">
                          {mediaItem.fileSize 
                            ? `${Math.round(mediaItem.fileSize / (1024 * 1024))} MB` 
                            : "-- MB"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                          <Button variant="ghost" size="sm">View</Button>
                          <Button variant="ghost" size="sm" className="text-destructive">Delete</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-slate-100 font-medium">No media files found</p>
                <p className="text-slate-400 text-sm mt-1">
                  {searchQuery ? 
                    "Try adjusting your search or filters" :
                    "Upload photos, videos, or documents to your library"}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="timeline">
          {isLoading ? (
            <Card>
              <CardContent className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-slate-300">Loading media timeline...</p>
              </CardContent>
            </Card>
          ) : Object.keys(groupedMedia).length > 0 ? (
            <div className="space-y-8">
              {Object.entries(groupedMedia).map(([dateGroup, media]: [string, any]) => (
                <div key={dateGroup}>
                  <h3 className="font-montserrat font-semibold text-lg mb-4 text-[#E0A458]">{dateGroup}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {media.map((mediaItem: any) => (
                      <MediaItem
                        key={mediaItem.id}
                        item={mediaItem}
                        thumbnailHeight="h-52"
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center flex flex-col items-center gap-3">
                <Upload className="h-8 w-8 text-[#E0A458]/50" />
                <p className="text-slate-200 font-semibold">No memories on the timeline yet.</p>
                <p className="text-slate-400 text-sm">
                  {searchQuery ? "Try adjusting your search or filters." : "Upload your first photo or video to start your visual journey."}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MediaLibrary;
